// [TOUR_LISTS_PAGE_V1] 안내내역 — 저장된 매물 리스트 목록 / 상세
//   기본 export : TourListsPage  (탭 "안내내역" 본체)
//   named export: TourSaveModal  (매물장에서 선택 매물을 저장하는 팝업)
//
//   연관: /api/tour-lists            (목록·상세·삭제)
//         /api/properties/by-estate-no (매물 상세 — 연락처 포함, premium/admin)
//         /api/sms-receivers          (고객 선택)
//         components/TourPlanner      (동선 계산 + 내비)
import { useState, useEffect, useCallback } from "react";
import TourPlanner from "./TourPlanner";
import PropertyList from "./PropertyList";
import EstateEditor from "./EstateEditor";   // [TOUR_EDITOR_V1] 상세보기 오버레이   // [TOUR_REUSE_LIST_V1] 매물장과 동일한 상세 카드 재사용

// ── 공통 호출 ───────────────────────────────────────────────
function tok() {
  return localStorage.getItem("token") || "";
}
async function api(path, opt = {}) {
  const res = await fetch(path, {
    ...opt,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${tok()}`,
      ...(opt.headers || {}),
    },
  });
  return res.json();
}

// ── 전화번호 자동 하이픈 (입력 중 실시간 적용) ──────────────
export function hyphen(t) {
  const d = String(t || "").replace(/\D/g, "").slice(0, 11);
  if (d.length < 4) return d;
  if (d.length < 8) return d.replace(/(\d{3})(\d+)/, "$1-$2");
  if (d.length === 10) return d.replace(/(\d{3})(\d{3})(\d+)/, "$1-$2-$3");
  return d.replace(/(\d{3})(\d{4})(\d+)/, "$1-$2-$3");
}

const fmtDay = (s) => String(s || "").slice(0, 10);

// ═══════════════════════════════════════════════════════════
// 저장 팝업 — 매물장에서 체크한 매물을 안내내역으로 저장
//   props: selected(배열), onClose, onSaved
// ═══════════════════════════════════════════════════════════
export function TourSaveModal({ selected = [], onClose, onSaved }) {
  const [customers, setCustomers] = useState([]);   // sms_receivers 중 category='고객'
  const [allLists, setAllLists] = useState([]);     // [TOUR_APPEND_V1] 전체 안내내역 (같은 고객 찾기용)
  const [appendId, setAppendId] = useState("");     // 추가할 기존 안내내역 id. "" 이면 새로 저장
  const [pastCust, setPastCust] = useState([]);     // [TOUR_CUST_V1] 지난 안내 고객 (수신처 미등록, tour_lists 에서)
  const [pickId, setPickId] = useState("");         // 드롭다운에서 고른 고객 id
  const [name, setName] = useState("");
  const [tel, setTel] = useState("");
  const [memo, setMemo] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // 고객 목록 로드. 서버에 category 필터가 없으므로 전체 받아 프런트에서 거른다.
  useEffect(() => {
    api("/api/sms-receivers")
      .then((r) => {
        const all = r.items || r.list || [];
        // [TOUR_DATE_DEL_V1] 서버(smsReceivers.js)는 이름순으로 줌 → 등록일 최근순으로 재정렬
        //   created_at 이 같거나 없으면 id 큰 것(나중 등록)이 위
        setCustomers(
          all
            .filter((x) => (x.category || "") === "고객")
            .sort((a, b) =>
              String(b.created_at || "").localeCompare(String(a.created_at || "")) ||
              (Number(b.id) || 0) - (Number(a.id) || 0)
            )
        );
      })
      .catch(() => {});

    // [TOUR_CUST_V1] 지난 안내 고객 — 수신처에 연결 안 된(직접 입력한) 고객명
    //   GET /api/tour-lists 는 최근순 → 이름+번호 기준 중복 제거 (가장 최근 것만 남음)
    api("/api/tour-lists?limit=200")
      .then((r) => {
        const seen = new Set();
        const out = [];
        (r.list || []).forEach((t) => {
          if (t.receiver_id || !t.customer_name) return;   // 수신처 연결된 건·이름 없는 건 제외
          const key = t.customer_name + "|" + (t.customer_tel || "");
          if (seen.has(key)) return;
          seen.add(key);
          out.push({ name: t.customer_name, phone: t.customer_tel || "", day: fmtDay(t.created_at) });   // [TOUR_DATE_DEL_V1] 날짜 추가
        });
        setPastCust(out);
        setAllLists(r.list || []);   // [TOUR_APPEND_V1]
      })
      .catch(() => {});
  }, []);

  // 드롭다운 선택 → 이름·연락처 자동 채움
  const pick = (id) => {
    setPickId(id);
    // [TOUR_CUST_V1] 값이 "t:0", "t:1" … 이면 지난 안내 고객(pastCust 의 순번), 숫자면 수신처 id
    const c = String(id).startsWith("t:")
      ? pastCust[Number(String(id).slice(2))]
      : customers.find((x) => String(x.id) === String(id));
    if (c) {
      setName(c.name || "");
      setTel(hyphen(c.phone || ""));
    } else {
      setName("");
      setTel("");
    }
  };

  const save = async () => {
    if (selected.length === 0) return setErr("선택된 매물이 없습니다");
    setBusy(true);
    setErr("");
    try {
      // [TOUR_APPEND_V1] 기존 안내내역에 매물 추가
      if (appendTarget) {
        const merged = [...new Set(
          [...(appendTarget.estate_nos || []), ...selected.map((p) => p.estate_no)].map(Number)
        )];                                                   // 같은 매물은 한 번만
        if (merged.length > 50) {
          setErr("한 안내내역에 50건까지 가능합니다 (합치면 " + merged.length + "건)");
          setBusy(false);
          return;
        }
        const body = { estate_nos: merged };
        if (memo.trim()) {                                    // 메모는 기존 뒤에 이어 붙임
          body.memo = appendTarget.memo ? appendTarget.memo + " / " + memo.trim() : memo.trim();
        }
        const r2 = await api("/api/tour-lists/" + appendTarget.id, {
          method: "PUT",
          body: JSON.stringify(body),
        });
        if (r2.ok) {
          onSaved && onSaved({ ok: true, id: appendTarget.id, title: appendTarget.title, count: merged.length, appended: true });
        } else {
          setErr(r2.message || "추가 실패");
        }
        setBusy(false);
        return;
      }
      const r = await api("/api/tour-lists", {
        method: "POST",
        body: JSON.stringify({
          estate_nos: selected.map((p) => p.estate_no),
          receiver_id: /^\d+$/.test(String(pickId)) ? pickId : null,   // [TOUR_CUST_V1] 숫자일 때만 수신처 id
          customer_name: name || null,
          customer_tel: tel || null,
          memo: memo || null,
        }),
      });
      if (r.ok) onSaved && onSaved(r);
      else setErr(r.message || "저장 실패");
    } catch {
      setErr("서버 연결 실패");
    }
    setBusy(false);
  };

  // [TOUR_APPEND_V1] 지금 고른(또는 입력한) 고객의 기존 안내내역
  //   - 드롭다운에서 고른 수신처 id 가 같거나
  //   - 이름 + 연락처(숫자만 비교) 가 같으면 같은 고객으로 봄
  //   - allLists 는 최근순이라 sameCust[0] 이 가장 최근 안내내역
  const digitsOf = (v) => String(v || "").replace(/\D/g, "");
  const sameCust = allLists.filter((t) => {
    if (/^\d+$/.test(String(pickId)) && String(t.receiver_id) === String(pickId)) return true;
    if (!name.trim()) return false;
    return (t.customer_name || "") === name.trim() && digitsOf(t.customer_tel) === digitsOf(tel);
  });
  const sameKey = sameCust.map((t) => t.id).join(",");
  useEffect(() => {
    // 고객이 바뀔 때마다 기본값 = 가장 최근 안내내역에 추가 (없으면 새로 저장)
    setAppendId(sameCust.length ? String(sameCust[0].id) : "");
  }, [sameKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const appendTarget = allLists.find((t) => String(t.id) === String(appendId));

  // 제목 미리보기 — 서버 규칙과 동일하게 맞춘다
  const today = new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10);
  const preview =
    name || tel ? [name, tel, `(${today})`].filter(Boolean).join(" ") : `${today} 동선`;

  return (
    <div style={M.dim} onClick={onClose}>
      <div style={M.box} onClick={(e) => e.stopPropagation()}>
        <div style={M.head}>
          <b>안내내역으로 저장</b>
          <button onClick={onClose} style={S.smBtn}>닫기</button>
        </div>

        <div style={M.count}>선택한 매물 {selected.length}건</div>

        <label style={M.lbl}>고객 선택 (SMS 수신처 · 지난 안내 고객)</label>
        <select value={pickId} onChange={(e) => pick(e.target.value)} style={M.inp}>
          <option value="">— 직접 입력 —</option>
          {customers.length > 0 && (
            <optgroup label="SMS 수신처 (최근 등록순)">
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.created_at ? fmtDay(c.created_at) + " · " : ""}{c.name} ({hyphen(c.phone)})
                </option>
              ))}
            </optgroup>
          )}
          {pastCust.length > 0 && (
            <optgroup label="지난 안내 고객 (수신처 미등록)">
              {pastCust.map((c, i) => (
                <option key={"t" + i} value={"t:" + i}>
                  {c.day ? c.day + " · " : ""}{c.name}{c.phone ? " (" + hyphen(c.phone) + ")" : ""}
                </option>
              ))}
            </optgroup>
          )}
        </select>

        <label style={M.lbl}>고객명</label>
        <input value={name} onChange={(e) => setName(e.target.value)} style={M.inp} placeholder="비워두면 날짜로 제목 생성" />

        <label style={M.lbl}>연락처</label>
        <input value={tel} onChange={(e) => setTel(hyphen(e.target.value))} style={M.inp} placeholder="010-0000-0000" />

        {sameCust.length > 0 && (
          <>
            <label style={M.lbl}>저장 방식</label>
            <select value={appendId} onChange={(e) => setAppendId(e.target.value)} style={M.inp}>
              {sameCust.map((t) => (
                <option key={t.id} value={t.id}>
                  기존 「{t.title}」에 매물 추가 (현재 {(t.estate_nos || []).length}건)
                </option>
              ))}
              <option value="">새 안내내역으로 저장</option>
            </select>
          </>
        )}

        <label style={M.lbl}>메모 (선택)</label>
        <input value={memo} onChange={(e) => setMemo(e.target.value)} style={M.inp} />

        {appendTarget ? (
          <div style={M.preview}>추가 대상: <b>{appendTarget.title}</b></div>
        ) : (
          <div style={M.preview}>제목: <b>{preview}</b></div>
        )}

        {err && <div style={S.err}>{err}</div>}

        <button onClick={save} disabled={busy} style={S.mainBtn}>
          {busy ? "저장 중..." : "저장"}
        </button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 안내내역 페이지 (탭 본체)
// ═══════════════════════════════════════════════════════════
export default function TourListsPage() {
  const [lists, setLists] = useState([]);
  const [openId, setOpenId] = useState(null);   // 상세를 펼친 리스트 id
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    const r = await api(`/api/tour-lists${q ? `?q=${encodeURIComponent(q)}` : ""}`);
    if (r.ok) setLists(r.list || []);
    setBusy(false);
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const remove = async (id, title) => {
    if (!window.confirm(`"${title}" 을(를) 삭제할까요?`)) return;
    const r = await api(`/api/tour-lists/${id}`, { method: "DELETE" });
    if (r.ok) { setOpenId(null); load(); }
  };

  return (
    <div style={S.page}>
      <h2 style={{ fontSize: 18, margin: "0 0 12px" }}>안내내역</h2>

      <div style={S.searchBox}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="제목·고객명·연락처 검색"
          style={S.input}
        />
        <button onClick={load} style={S.smBtn}>새로고침</button>
      </div>

      {busy && <div style={S.hint}>불러오는 중...</div>}
      {!busy && lists.length === 0 && (
        <div style={S.hint}>저장된 안내내역이 없습니다. 매물장에서 매물을 선택해 저장하세요.</div>
      )}

      {lists.map((l) => (
        <div key={l.id} style={S.card}>
          <div style={S.cardHead} onClick={() => setOpenId(openId === l.id ? null : l.id)}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={S.title}>{fmtDay(l.created_at)} · {l.title}</div>
              <div style={S.sub}>
                매물 {l.estate_count}건
                {l.updated_at && fmtDay(l.updated_at) !== fmtDay(l.created_at) ? " · 수정 " + fmtDay(l.updated_at) : ""}
                {l.memo ? ` · ${l.memo}` : ""}
              </div>
            </div>
            <span style={S.caret}>{openId === l.id ? "▲" : "▼"}</span>
          </div>

          {openId === l.id && (
            <TourDetail
              list={l}
              onDelete={() => remove(l.id, l.title)}
              onChanged={(nos) =>
                setLists((ls) => ls.map((x) => (x.id === l.id ? { ...x, estate_nos: nos, estate_count: nos.length } : x)))
              }
            />
          )}
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// 상세 — 매물 카드 + 동선 만들기
//   매물 정보는 기존 API 로 개별 조회한다(마스킹 정책 재사용).
// ═══════════════════════════════════════════════════════════
function TourDetail({ list, onDelete, onChanged }) {   // [TOUR_DATE_DEL_V1] onChanged = 매물 제외 후 목록 갱신
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [planner, setPlanner] = useState(false);
  // [TOUR_EDITOR_V1] 상세보기 오버레이 상태
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorEstate, setEditorEstate] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setBusy(true);
      const out = [];
      // 매물번호 하나씩 조회. 보통 3~8건이라 순차로 충분하다.
      for (const no of list.estate_nos || []) {
        try {
          const r = await api(`/api/properties/by-estate-no?estate_no=${no}`, { method: "GET" });
          const it = (r.items || [])[0];
          if (it) {
            // DB의 DECIMAL 컬럼은 문자열로 온다. 구버전 컬럼(map_y/map_x)도 대비.
            it.lat = Number(it.lat) || Number(it.map_y) || null;
            it.lng = Number(it.lng) || Number(it.map_x) || null;
            out.push(it);
          }
        } catch { /* 한 건 실패해도 나머지는 계속 */ }
      }
      if (alive) { setItems(out); setBusy(false); }
    })();
    return () => { alive = false; };
  }, [list.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── [TOUR_DATE_DEL_V1] 매물 하나를 이 안내내역에서 빼기 ─────────────
  //   - 매물장의 매물은 지우지 않음. tour_lists.estate_nos 배열에서만 빠짐
  //   - list.estate_nos 기준으로 계산 (조회에 실패해 화면에 안 보이는 매물번호도 보존)
  //   - 마지막 1건이면 PUT 이 400(매물이 비었습니다) → 안내내역 전체 삭제로 연결
  const [removing, setRemoving] = useState(null);   // 제외 처리 중인 매물번호 (버튼 중복 클릭 방지)
  const nameOf = (p) =>
    [
      p.estate_name,
      p.estate_dong ? p.estate_dong + "동" : "",
      p.estate_ho && String(p.estate_ho) !== "0" ? p.estate_ho + "호" : "",   // 호수 0 은 '모름' 자리값
    ].filter(Boolean).join(" ") || p.jibunAddress || "";
  const exclude = async (p) => {
    const no = Number(p.estate_no);
    if (!no) { window.alert("매물번호를 찾지 못했습니다"); return; }
    const nos = (list.estate_nos || []).map(Number).filter((n) => n !== no);
    if (nos.length === 0) {
      window.alert("마지막 매물입니다. 빼려면 안내내역 전체를 삭제해야 합니다.");
      onDelete && onDelete();                         // 기존 삭제 확인창으로
      return;
    }
    if (!window.confirm(no + " " + nameOf(p) + "\n이 매물을 안내내역에서 뺄까요? (매물장의 매물은 그대로 남습니다)")) return;
    setRemoving(no);
    try {
      const r = await api("/api/tour-lists/" + list.id, {
        method: "PUT",
        body: JSON.stringify({ estate_nos: nos }),
      });
      if (r.ok) {
        setItems((xs) => xs.filter((x) => Number(x.estate_no) !== no));   // 화면에서 즉시 제거
        onChanged && onChanged(nos);                                       // 목록 카드 「매물 N건」 갱신
      } else {
        window.alert(r.message || "제외 실패");
      }
    } catch {
      window.alert("서버 연결 실패");
    }
    setRemoving(null);
  };

  if (planner) {
    return (
      <div style={S.detail}>
        <TourPlanner selected={items} onClose={() => setPlanner(false)} />
      </div>
    );
  }

  return (
    <div style={S.detail}>
      {busy && <div style={S.hint}>매물 불러오는 중...</div>}

      {!busy && items.length > 0 && (
        <div style={S.exBox}>
          <div style={S.exHead}>안내 매물 {items.length}건 · 뺄 매물은 [제외]</div>
          {items.map((p) => (
            <div key={p.estate_no} style={S.exRow}>
              <span style={S.exName}>{p.estate_no} · {nameOf(p)}</span>
              <button onClick={() => exclude(p)} disabled={removing === Number(p.estate_no)} style={S.exBtn}>
                {removing === Number(p.estate_no) ? "..." : "제외"}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* 매물장과 동일한 상세 카드. 행을 클릭하면 펼쳐진다. */}
      {!busy && items.length > 0 && (
        <PropertyList
          properties={items}
          loading={false}
          onRefresh={() => {}}
          onEditEstate={(p) => { setEditorEstate(p); setEditorOpen(true); }}
        />
      )}

      {!busy && (
        <div style={S.detailBtns}>
          <button onClick={() => setPlanner(true)} style={S.mainBtn}>
            동선 만들기 ({items.length}곳)
          </button>
          <button onClick={onDelete} style={S.delBtn}>이 안내내역 삭제</button>
        </div>
      )}

      {/* [TOUR_EDITOR_V1] 이미 받아둔 매물 객체를 그대로 넘긴다(추가 조회 없음) */}
      {editorOpen && (
        <EstateEditor
          open={editorOpen}
          mode="view"
          estate={editorEstate}
          onClose={() => { setEditorOpen(false); setEditorEstate(null); }}
          onSaved={() => { setEditorOpen(false); setEditorEstate(null); }}
        />
      )}
    </div>
  );
}

// ── 스타일 ──────────────────────────────────────────────────
const S = {
  page: { padding: 16, maxWidth: 720, margin: "0 auto", fontSize: 14 },
  searchBox: { display: "flex", gap: 6, marginBottom: 14 },
  input: { flex: 1, padding: "8px 10px", border: "1px solid #ccc", borderRadius: 6, fontSize: 14 },
  smBtn: { padding: "6px 12px", fontSize: 13, border: "1px solid #ccc", background: "#fff", borderRadius: 6, cursor: "pointer" },
  mainBtn: { width: "100%", padding: "11px 0", fontSize: 15, fontWeight: 600, border: "1px solid #2a78d6", background: "#2a78d6", color: "#fff", borderRadius: 8, cursor: "pointer" },
  // [TOUR_DATE_DEL_V1] 매물 제외 목록
  exBox:  { border: "1px solid #eee", borderRadius: 8, padding: "6px 10px", marginBottom: 10, background: "#fafafa" },
  exHead: { fontSize: 12, color: "#888", padding: "4px 0" },
  exRow:  { display: "flex", alignItems: "center", gap: 8, padding: "5px 0", borderTop: "1px dashed #e5e5e5" },
  exName: { flex: 1, minWidth: 0, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  exBtn:  { padding: "4px 10px", fontSize: 12, border: "1px solid #e0b4b4", background: "#fff", color: "#c0392b", borderRadius: 6, cursor: "pointer", flexShrink: 0 },
  delBtn: { width: "100%", padding: "9px 0", fontSize: 13, border: "1px solid #e0b4b4", background: "#fff", color: "#c0392b", borderRadius: 8, cursor: "pointer", marginTop: 8 },
  err: { margin: "10px 0", padding: "8px 12px", background: "#fdecea", color: "#c0392b", borderRadius: 6, fontSize: 13 },
  hint: { padding: "16px 4px", color: "#888", fontSize: 13 },
  card: { border: "1px solid #e5e7eb", borderRadius: 10, marginBottom: 10, overflow: "hidden", background: "#fff" },
  cardHead: { display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", cursor: "pointer" },
  title: { fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  sub: { fontSize: 12, color: "#777", marginTop: 3 },
  caret: { color: "#aaa", fontSize: 12, flexShrink: 0 },
  detail: { borderTop: "1px solid #eee", padding: "12px 14px", background: "#fafafa" },
  detailBtns: { marginTop: 12 },
  pcard: { border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 12px", marginBottom: 8, background: "#fff" },
  pname: { fontWeight: 600 },
  pno: { fontSize: 11, color: "#999", fontWeight: 400 },
  paddr: { fontSize: 12, color: "#777", marginTop: 2 },
  pprice: { fontSize: 14, color: "#2a78d6", fontWeight: 600, marginTop: 4 },
  pmeta: { fontSize: 12, color: "#666", marginTop: 3 },
  tel: { display: "inline-block", marginTop: 6, marginRight: 8, padding: "4px 10px", fontSize: 12, border: "1px solid #2a78d6", color: "#2a78d6", borderRadius: 14, textDecoration: "none" },
};

const M = {
  dim: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16 },
  box: { background: "#fff", borderRadius: 12, padding: 18, width: "100%", maxWidth: 420, maxHeight: "90vh", overflowY: "auto", fontSize: 14 },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 },
  count: { fontSize: 13, color: "#2a78d6", marginBottom: 12 },
  lbl: { display: "block", fontSize: 12, color: "#666", margin: "10px 0 4px" },
  inp: { width: "100%", padding: "8px 10px", border: "1px solid #ccc", borderRadius: 6, fontSize: 14, boxSizing: "border-box" },
  preview: { margin: "14px 0", padding: "8px 12px", background: "#f3f4f6", borderRadius: 6, fontSize: 13 },
};
